import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import StatsCard from '../../components/common/StatsCard';
import { CreditCard, DollarSign, TrendingUp, PieChart } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

type Fee = {
  id: string;
  type: 'performance' | 'management' | 'subscription' | 'success' | 'referral';
  description: string;
  amount: number;
  fromUser: string;
  toUser: string;
  date: string;
  status: 'paid' | 'pending' | 'failed';
  service: 'copy_trading' | 'pamm' | 'mam';
};

const TradingFees = () => {
  const [feeTypeFilter, setFeeTypeFilter] = useState<
    | 'all'
    | 'performance'
    | 'management'
    | 'subscription'
    | 'success'
    | 'referral'
  >('all');

  const fees: Fee[] = [
    {
      id: 'FEE001',
      type: 'performance',
      description: 'Performance fee from Gold Scalper strategy',
      amount: 1250.0,
      fromUser: 'John Smith',
      toUser: 'Platform',
      date: '2024-01-15',
      status: 'paid',
      service: 'copy_trading',
    },
    {
      id: 'FEE002',
      type: 'management',
      description: 'Monthly management fee for Alpha Growth Fund',
      amount: 500.0,
      fromUser: 'Jane Doe',
      toUser: 'Mike Johnson',
      date: '2024-01-15',
      status: 'paid',
      service: 'pamm',
    },
    {
      id: 'FEE003',
      type: 'subscription',
      description: 'Monthly subscription fee for Forex Trend Master',
      amount: 150.0,
      fromUser: 'Sarah Williams',
      toUser: 'David Brown',
      date: '2024-01-14',
      status: 'paid',
      service: 'copy_trading',
    },
    {
      id: 'FEE004',
      type: 'success',
      description: 'Success fee from profitable trades',
      amount: 890.0,
      fromUser: 'Tom Davis',
      toUser: 'Platform',
      date: '2024-01-14',
      status: 'pending',
      service: 'mam',
    },
    {
      id: 'FEE005',
      type: 'referral',
      description: 'Referral commission from new investor',
      amount: 250.0,
      fromUser: 'Platform',
      toUser: 'Emily Chen',
      date: '2024-01-13',
      status: 'paid',
      service: 'pamm',
    },
    {
      id: 'FEE006',
      type: 'performance',
      description: 'Performance fee from Crypto Alpha strategy',
      amount: 2100.0,
      fromUser: 'Robert Wilson',
      toUser: 'Platform',
      date: '2024-01-13',
      status: 'paid',
      service: 'copy_trading',
    },
  ];

  const filteredFees = fees.filter(
    fee => feeTypeFilter === 'all' || fee.type === feeTypeFilter
  );

  const feeColumns: DataTableColumn<Fee>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '100px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'performance'
              ? 'bg-blue-100 text-blue-800'
              : row.type === 'management'
                ? 'bg-purple-100 text-purple-800'
                : row.type === 'subscription'
                  ? 'bg-green-100 text-green-800'
                  : row.type === 'success'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-pink-100 text-pink-800'
          }`}
        >
          {row.type.replace('_', ' ').toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '140px',
    },
    {
      id: 'description',
      header: 'Description',
      accessor: row => row.description,
      sortable: true,
      sortValue: row => row.description,
      width: '250px',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => `$${row.amount.toFixed(2)}`,
      sortable: true,
      sortValue: row => row.amount,
      width: '120px',
    },
    {
      id: 'fromUser',
      header: 'From',
      accessor: row => row.fromUser,
      sortable: true,
      sortValue: row => row.fromUser,
      width: '150px',
    },
    {
      id: 'toUser',
      header: 'To',
      accessor: row => row.toUser,
      sortable: true,
      sortValue: row => row.toUser,
      width: '150px',
    },
    {
      id: 'service',
      header: 'Service',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.service === 'copy_trading'
              ? 'bg-blue-100 text-blue-800'
              : row.service === 'pamm'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-orange-100 text-orange-800'
          }`}
        >
          {row.service.replace('_', ' ').toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.service,
      width: '120px',
    },
    {
      id: 'date',
      header: 'Date',
      accessor: row => new Date(row.date).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.date,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'paid'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '100px',
    },
  ];

  const totalFees = filteredFees.reduce((sum, fee) => sum + fee.amount, 0);
  const paidFees = filteredFees
    .filter(fee => fee.status === 'paid')
    .reduce((sum, fee) => sum + fee.amount, 0);
  const pendingFees = filteredFees
    .filter(fee => fee.status === 'pending')
    .reduce((sum, fee) => sum + fee.amount, 0);

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Fees Overview"
          description="Track all fees across trading services"
        />
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance Fees</TabsTrigger>
          <TabsTrigger value="management">Management Fees</TabsTrigger>
          <TabsTrigger value="subscription">Subscription Fees</TabsTrigger>
          <TabsTrigger value="history">Fee History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatsCard
              title="Total Fees"
              value={`$${totalFees.toFixed(2)}`}
              change="All time"
              icon={CreditCard}
            />
            <StatsCard
              title="Paid Fees"
              value={`$${paidFees.toFixed(2)}`}
              change="Completed transactions"
              icon={DollarSign}
            />
            <StatsCard
              title="Pending Fees"
              value={`$${pendingFees.toFixed(2)}`}
              change="Awaiting processing"
              icon={TrendingUp}
            />
            <StatsCard
              title="Fee Transactions"
              value={filteredFees.length.toString()}
              change="Total records"
              icon={PieChart}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  $
                  {filteredFees
                    .filter(f => f.type === 'performance')
                    .reduce((sum, f) => sum + f.amount, 0)
                    .toFixed(2)}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {filteredFees.filter(f => f.type === 'performance').length}{' '}
                  transactions
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Management Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  $
                  {filteredFees
                    .filter(f => f.type === 'management')
                    .reduce((sum, f) => sum + f.amount, 0)
                    .toFixed(2)}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {filteredFees.filter(f => f.type === 'management').length}{' '}
                  transactions
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Subscription Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  $
                  {filteredFees
                    .filter(f => f.type === 'subscription')
                    .reduce((sum, f) => sum + f.amount, 0)
                    .toFixed(2)}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {filteredFees.filter(f => f.type === 'subscription').length}{' '}
                  transactions
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Fees</CardTitle>
              <CardDescription>
                Fees charged based on trading performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={feeColumns}
                data={filteredFees.filter(f => f.type === 'performance')}
                searchPlaceholder="Search performance fees..."
                showColumnVisibility={true}
                showExportButton={false}
                showAddButton={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="management" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Management Fees</CardTitle>
              <CardDescription>
                Regular management fees for fund management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={feeColumns}
                data={filteredFees.filter(f => f.type === 'management')}
                searchPlaceholder="Search management fees..."
                showColumnVisibility={true}
                showExportButton={false}
                showAddButton={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Fees</CardTitle>
              <CardDescription>
                Monthly subscription fees for copy trading
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={feeColumns}
                data={filteredFees.filter(f => f.type === 'subscription')}
                searchPlaceholder="Search subscription fees..."
                showColumnVisibility={true}
                showExportButton={false}
                showAddButton={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <button
              className={`px-3 py-1 text-sm rounded-md ${feeTypeFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
              onClick={() => setFeeTypeFilter('all')}
            >
              All Types
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-md ${feeTypeFilter === 'performance' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
              onClick={() => setFeeTypeFilter('performance')}
            >
              Performance
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-md ${feeTypeFilter === 'management' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
              onClick={() => setFeeTypeFilter('management')}
            >
              Management
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-md ${feeTypeFilter === 'subscription' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
              onClick={() => setFeeTypeFilter('subscription')}
            >
              Subscription
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-md ${feeTypeFilter === 'success' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
              onClick={() => setFeeTypeFilter('success')}
            >
              Success
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-md ${feeTypeFilter === 'referral' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}
              onClick={() => setFeeTypeFilter('referral')}
            >
              Referral
            </button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Fee History</CardTitle>
              <CardDescription>
                Complete fee transaction history
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={feeColumns}
                data={filteredFees}
                searchPlaceholder="Search fees..."
                showColumnVisibility={true}
                showExportButton={false}
                showAddButton={false}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TradingFees;
